import ActiveUsersIcon from '@ping/assets/Icon/active-users.svg';
import ExceptionalCasesIcon from '@ping/assets/Icon/exceptional-cases.svg';
import HeavyLoadsIcon from '@ping/assets/Icon/heavy-loads.svg';
import TradingLimitsIcon from '@ping/assets/Icon/trading-limits.svg';
import UpgradesIcon from '@ping/assets/Icon/upgrades.svg';
import { APP_VERSION } from '@ping/configs';
import { modalKeys } from '@ping/constants/modal-keys.constant';
import { t } from '@ping/helpers';
import { useRemoteModal } from '@ping/hooks';
import { isSelectedThemeDarkSelector, usePreferencesStore } from '@ping/stores/preferences.store';
import { ButtonA11Y, Modal } from '@ping/uikit';

import style from './style.module.scss';

interface IRestrictionItemProps {
  icon: React.ReactNode;
  title: string;
  description: React.ReactNode;
}

const RestrictionItem = ({ icon, title, description }: IRestrictionItemProps) => (
  <div className={style['restriction-item']}>
    <div className={style['restriction-item--icon']}>{icon}</div>
    <div className={style['restriction-item--content']}>
      <h6 className={style['restriction-item--title']}>{title}</h6>
      <p className={style['restriction-item--description']}>{description}</p>
    </div>
  </div>
);

export const ImportantNoticeModal = () => {
  const isSelectedThemeDark = usePreferencesStore(isSelectedThemeDarkSelector);
  const modal = useRemoteModal(modalKeys.IMPORTANT_NOTICE);

  return (
    <Modal.Root {...modal.props}>
      <Modal.Portal className={style['important-notice-modal']} divide isDismissable isKeyboardDismissDisabled>
        <Modal.Header className={style['important-notice-modal__header']}>
          <img
            className={style['important-notice-modal__logo']}
            src={`/img/logo-${APP_VERSION.toLowerCase()}-${isSelectedThemeDark ? 'dark' : 'light'}.svg`}
            alt='logo'
          />
        </Modal.Header>
        <Modal.Content className={style['important-notice-modal__content']}>
          <h4 className={style['important-notice-modal__title']}>{t('Important Trading Notice!')}</h4>
          <p className={style['important-notice-modal__description']}>
            {t(
              'Please note that Ping Exchange is currently operating as an Alpha Release. While this does not influence the security of the platform, the security of your funds, or your personal information, there are several minor restrictions that you might experience during your trading, such as:'
            )}
          </p>
          <div className={style['important-notice-modal__restrictions']}>
            <RestrictionItem
              icon={<UpgradesIcon />}
              title={t('Regular system upgrades')}
              description={t(
                'Maintenance and upgrades might cause limitations in the function of the platform, disabling all activities, including Trading, Deposits, Withdrawals, etc.'
              )}
            />
            <RestrictionItem
              icon={<HeavyLoadsIcon />}
              title={t('Heavy loads')}
              description={t(
                'Should the platform experience excessive strain due to high turnouts, users might be unable to trade, deposit, or withdraw for a brief period of time, of which they shall be notified in advance. Reviewing your activity is advised in this case.'
              )}
            />
            <RestrictionItem
              icon={<ActiveUsersIcon />}
              title={t('Active users list')}
              description={t(
                'Ping Exchange will operate with a limited capacity of active users and orders per minute. The limit shall gradually increase with future updates.'
              )}
            />
            <RestrictionItem
              icon={<TradingLimitsIcon />}
              title={t('Trading limits')}
              description={t(
                'An upper limit for trades is likely to be introduced to secure the platform’s stability and the security of all funds.'
              )}
            />
            <RestrictionItem
              icon={<ExceptionalCasesIcon />}
              title={t('Exceptional Cases - Market Closure')}
              description={
                <>
                  {t('In very exceptional cases, the Alpha release might face unforeseen issues, such as:')}
                  <ul className={style['restriction-item--description-list']}>
                    <li>{t('Malicious activity')}</li>
                    <li>{t('A flaw that needs immediate fixing')}</li>
                    <li>{t('Other high-profile risks')}</li>
                  </ul>
                  {t(
                    'This might lead to an immediate halt on all the markets, as well as market orders, due to the platform’s commitment to follow the highest security standards and eliminate any risk of larceny of users’ funds.'
                  )}
                </>
              }
            />
          </div>
        </Modal.Content>

        <Modal.Actions className={style['important-notice-modal__actions']}>
          <ButtonA11Y className={style['important-notice-modal__agree']} onPress={modal.state.close}>
            {t('Agree & Continue')}
          </ButtonA11Y>
        </Modal.Actions>
      </Modal.Portal>
    </Modal.Root>
  );
};
